/**
 * **One booking flow, on one simple page** — `/site/booking/:flowId` and
 * `/site/booking/new` (C20, FR-J11, ADR-0040 §5; Wave 20C, T-5.68). The
 * owner, 2026-09-27: *"current booking flow is not user friendly, ui ux is
 * very complex"*.
 *
 * The page is the flow's name, address and status, then **five cards**, saved
 * together by **one `EditBar`** (`PUT /booking-flows/{id}`, or `POST` for a
 * new one), with `useLeaveGuard` so nothing unsaved is lost:
 *
 * 1. **What we ask** — name and email (always, shown as fixed rows), *Ask for
 *    mobile*, *Verify mobile by code* (off, with the vocabulary's reason, when
 *    the firm has no mobile channel or the mobile is not asked), and up to
 *    five extra questions;
 * 2. **What they can book** — services, and for each which plans (every plan
 *    the website shows ticked until the firm unticks one). A service the flow
 *    names that is **no longer offered** — inactive, or in an inactive
 *    category — is flagged, and a plan kept off the website is offered only
 *    when ticked (C25, ADR-0054 §2, §5);
 * 3. **Documents** — up to five files the visitor sends (`DocumentsCard`,
 *    C25, ADR-0053 §1);
 * 4. **Payment** — none, the gateway or a bank QR, the QR and its payee text,
 *    *Payment proof required* (`PaymentCard`, C25, ADR-0052 §1), and *Open a
 *    case when booked*;
 * 5. **Wording** — the title, intro, thank-you message and four buttons,
 *    each prefilled.
 *
 * The cards follow the order the visitor meets them in. An old flow — saved
 * before C25, with only Wave 20's `payment` boolean — opens with its method
 * (`paymentModeOf`), and its first Save sends both.
 *
 * **There is no step library, no ordering and no per-step labels.** The order
 * a visitor meets is ADR-0040 §1's, and the editor says it in one line at the
 * top (`FLOW_EXPLAINER`).
 *
 * **See it** opens `https://<domain>/book/<slug>` for a live or paused flow,
 * and the staff preview (w19e) for a draft, which the public read still
 * answers 404 for. It shows what is **saved**.
 */

import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  ConfirmDialog,
  Input,
  RadioGroup,
  Select,
  Skeleton,
  Textarea,
  buttonClass,
} from '@fiveox/ui'
import { ArrowDown, ArrowUp, ExternalLink, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'

import { EditBar, EditPage, useLeaveGuard } from '../../../components/edit'
import { FORM_GRID, FormRow, StatusBadge } from '../../../components/page'
import { BOOKING, SITE, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import {
  type BookingFlow,
  FLOW_EXPLAINER,
  FLOW_STATUS_NOTES,
  type FlowAsk,
  type FlowBody,
  type FlowOffer,
  type FlowQuestion,
  type FlowStatus,
  type FlowVocabulary,
  type FlowWording,
  MAX_DOCUMENTS,
  QR_WORDING_KEYS,
  QUESTION_KIND_LABELS,
  type QuestionKind,
  WORDING_KEYS,
  WORDING_NAMES,
  booking,
  cleanBody,
  flowProblems,
  nextQuestionKey,
  paymentModeOf,
  slugFromName,
  tickedPlans,
  togglePlan,
} from '../../../lib/api/booking'
import { type DomainBinding, firm } from '../../../lib/api/firm'
import { type Service, type ServiceCategory, planShown, services as servicesApi } from '../../../lib/api/services'
import { CardHead, SwitchRow } from './cardParts'
import { DocumentsCard, cleanDocuments, documentProblems } from './DocumentsCard'
import { EMPTY_QR, PaymentCard, cleanPaymentQr, paymentProblems } from './PaymentCard'

// -- the route ---------------------------------------------------------------

export function SiteBookingFlow() {
  const { flowId = 'new' } = useParams()
  const { can } = useEvaluation()
  const navigate = useNavigate()
  const isNew = flowId === 'new'

  const [vocab, setVocab] = useState<FlowVocabulary | null>(null)
  const [flow, setFlow] = useState<BookingFlow | null>(null)
  const [domain, setDomain] = useState<DomainBinding | null>(null)
  const [catalog, setCatalog] = useState<Service[] | null>(null)
  const [categories, setCategories] = useState<ServiceCategory[] | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'failed'>('loading')
  const [failure, setFailure] = useState<string | null>(null)
  // The flow a first save just made: its new address needs no second read.
  const created = useRef<BookingFlow | null>(null)

  useEffect(() => {
    let live = true
    // The catalog and the domain are each optional: without them the services
    // card says it could not read the catalog, and *See it* says why it is off.
    firm
      .domain()
      .then((body) => live && setDomain(body.data))
      .catch(() => undefined)
    // C25 (waves.md Wave 27 I-5): `live` — active and inactive — so a service
    // this flow names that has been paused is still drawn, and flagged.
    servicesApi
      .list('live')
      .then((body) => {
        if (!live) return
        setCatalog(body.data.services)
        setCategories(body.data.categories)
      })
      .catch(() => live && setCatalog(null))
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    let live = true
    if (created.current && created.current.id === flowId) {
      setFlow(created.current)
      created.current = null
      setState('ready')
      return
    }
    setState('loading')
    Promise.all([booking.vocabulary(), isNew ? Promise.resolve(null) : booking.flow(flowId)])
      .then(([v, f]) => {
        if (!live) return
        setVocab(v.data)
        setFlow(f ? f.data : null)
        setState('ready')
      })
      .catch((err: unknown) => {
        if (!live) return
        setFailure(err instanceof ApiError ? err.message : null)
        setState(err instanceof ApiError && err.status === 404 ? 'missing' : 'failed')
      })
    return () => {
      live = false
    }
  }, [flowId, isNew])

  const back = { to: '/site/booking', label: 'Booking flows' }

  if (state === 'loading') {
    return (
      <EditPage title="Loading the flow…" back={back}>
        <Skeleton className="h-64 rounded-xl" />
      </EditPage>
    )
  }
  if (state !== 'ready' || !vocab) {
    return (
      <EditPage title="Booking flow" back={back}>
        <Alert tone="error">
          {state === 'missing'
            ? 'This flow does not exist any more — it may have been deleted.'
            : (failure ?? 'This flow could not be loaded. Reload the page to try again.')}{' '}
          <Link to="/site/booking" className="font-medium underline">
            Back to booking flows
          </Link>
        </Alert>
      </EditPage>
    )
  }

  return (
    <FlowEditor
      key={flow?.id ?? 'new'}
      flow={flow}
      vocab={vocab}
      host={domain?.state === 'bound' ? domain.host : null}
      catalog={catalog}
      categories={categories}
      editable={can(BOOKING, 'can_update')}
      mediaEditable={can(SITE, 'can_update')}
      onCreate={async (body) => (await booking.createFlow(body)).data}
      onUpdate={async (id, body) => (await booking.updateFlow(id, body)).data}
      onDelete={(id) => booking.deleteFlow(id)}
      onCreated={(made) => {
        created.current = made
        navigate(`/site/booking/${made.id}`, { replace: true })
      }}
      onDeleted={() => navigate('/site/booking')}
    />
  )
}

// -- the values ------------------------------------------------------------------

/** Every wording text filled: the flow's own, else the default — and C25's
 *  two QR texts whenever the vocabulary has them (the payment card draws them). */
function fullWording(wording: Partial<FlowWording> | undefined, vocab: FlowVocabulary): FlowWording {
  const keys = [...WORDING_KEYS, ...QR_WORDING_KEYS.filter((key) => vocab.wording[key])]
  return Object.fromEntries(
    keys.map((key) => [key, wording?.[key] || vocab.default.wording[key] || vocab.wording[key]?.default || '']),
  ) as FlowWording
}

export function flowValues(flow: BookingFlow | null, vocab: FlowVocabulary): FlowBody {
  const base = flow ?? { ...structuredClone(vocab.default), name: '', slug: '', status: 'draft' as FlowStatus }
  return {
    name: base.name,
    slug: base.slug,
    status: base.status,
    ask: {
      mobile: base.ask.mobile,
      verify_mobile: base.ask.verify_mobile,
      questions: base.ask.questions.map((q) => ({ ...q })),
    },
    offer: { services: base.offer.services.map((s) => ({ ...s, plan_ids: [...s.plan_ids] })) },
    // C25 (I-1) — one method; a flow from before it is read by its boolean.
    payment: paymentModeOf(base) !== 'none',
    payment_mode: paymentModeOf(base),
    payment_qr: base.payment_qr ? { ...EMPTY_QR, ...base.payment_qr } : null,
    proof_required: base.proof_required ?? true,
    documents: (base.documents ?? []).map((d) => ({
      key: d.key,
      label: d.label,
      hint: d.hint ?? '',
      required: Boolean(d.required),
    })),
    create_case: base.create_case,
    wording: fullWording(base.wording, vocab),
  }
}

/** The draft as Save sends it: `cleanBody`, then C25's parts cleaned too —
 *  `payment` agreeing with the method, the QR trimmed (or `null` when empty),
 *  each document's label and hint trimmed. */
export function flowBody(values: FlowBody): FlowBody {
  const out = cleanBody(values)
  const mode = paymentModeOf(out)
  return {
    ...out,
    payment: mode !== 'none',
    payment_mode: mode,
    payment_qr: cleanPaymentQr(out.payment_qr),
    proof_required: out.proof_required ?? true,
    documents: cleanDocuments(out.documents ?? []),
  }
}

/** Everything that stops Save — the first is what the edit bar says. */
export function allProblems(body: FlowBody, vocab: FlowVocabulary): string[] {
  return [
    ...flowProblems(body, vocab),
    ...documentProblems(body.documents ?? [], vocab.max_documents ?? MAX_DOCUMENTS),
    ...paymentProblems(body),
  ]
}

/** Why *See it* is off, or `null` when it may open. A draft may: it opens
 *  the staff preview (w19e). */
export function seeItRefusal(saved: BookingFlow | null, host: string | null): string | null {
  if (!saved) return 'Save the flow first — then you can see it.'
  if (saved.status === 'draft') return null
  if (!host) return 'Bookings run on your own domain, and your website has none yet.'
  return null
}

/** Where *See it* goes once it may open: a draft to the staff preview, which
 *  only this firm's staff can open; anything else to the real page. */
export function seeItHref(saved: BookingFlow, host: string | null): string {
  if (saved.status === 'draft' || !host) return `/site/booking/${saved.id}/preview`
  return `https://${host}${saved.path}`
}

/** What a draft's *See it* says about itself. */
export const DRAFT_PREVIEW_NOTE = 'A draft is not on your website yet — See it opens a preview only your firm can see.'

/** Why *Verify mobile by code* cannot be turned on, or `null`. */
export function verifyMobileRefusal(ask: FlowAsk, vocab: FlowVocabulary): string | null {
  if (!vocab.mobile_channel.available) {
    return vocab.mobile_channel.reason ?? 'Your firm has no SMS or WhatsApp sender connected.'
  }
  if (!ask.mobile) return 'Turn on Ask for mobile first.'
  return null
}

// -- the editor ----------------------------------------------------------------

export function FlowEditor({
  flow,
  vocab,
  host,
  catalog,
  categories,
  editable,
  mediaEditable = false,
  onCreate,
  onUpdate,
  onDelete,
  onCreated,
  onDeleted,
}: {
  /** `null` for a new flow. */
  flow: BookingFlow | null
  vocab: FlowVocabulary
  /** The firm's bound domain, or `null`. */
  host: string | null
  /** The live services (active and inactive), or `null` when the catalog
   *  could not be read. */
  catalog: Service[] | null
  /** The catalog's categories — what says whether a service's category is
   *  active. Absent, every category is read as active. */
  categories?: ServiceCategory[] | null
  editable: boolean
  /** `site:can_update` — whether the QR picker may upload a picture. */
  mediaEditable?: boolean
  onCreate: (body: FlowBody) => Promise<BookingFlow>
  onUpdate: (id: string, body: FlowBody) => Promise<BookingFlow>
  onDelete: (id: string) => Promise<void>
  onCreated: (flow: BookingFlow) => void
  onDeleted: () => void
}) {
  const [saved, setSaved] = useState<BookingFlow | null>(flow)
  const [values, setValues] = useState<FlowBody>(() => flowValues(flow, vocab))
  // A new flow's address follows its name until the firm types one.
  const [slugTouched, setSlugTouched] = useState(Boolean(flow))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [then, setThen] = useState<{ kind: 'created'; flow: BookingFlow } | { kind: 'deleted' } | null>(null)

  const body = useMemo(() => flowBody(values), [values])
  const baseline = useMemo(() => JSON.stringify(flowBody(flowValues(saved, vocab))), [saved, vocab])
  const dirty = editable && then === null && JSON.stringify(body) !== baseline
  const guard = useLeaveGuard(dirty)
  const problems = useMemo(() => allProblems(body, vocab), [body, vocab])

  const after = useRef({ onCreated, onDeleted })
  after.current = { onCreated, onDeleted }
  useEffect(() => {
    if (!then) return
    if (then.kind === 'created') after.current.onCreated(then.flow)
    else after.current.onDeleted()
  }, [then])

  const set = <K extends keyof FlowBody>(key: K, value: FlowBody[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }))
  const setAsk = (ask: Partial<FlowAsk>) => setValues((prev) => ({ ...prev, ask: { ...prev.ask, ...ask } }))

  async function save() {
    if (problems.length > 0) return
    setSaving(true)
    setError(null)
    try {
      if (saved) {
        const next = await onUpdate(saved.id, body)
        setSaved(next)
        setValues(flowValues(next, vocab))
      } else {
        const made = await onCreate(body)
        setSaved(made)
        setValues(flowValues(made, vocab))
        setThen({ kind: 'created', flow: made })
      }
    } catch (err) {
      // The service's refusal, as it said it — its words are the firm's.
      setError(err instanceof ApiError ? err.message : 'That flow could not be saved. Try again.')
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (!saved) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await onDelete(saved.id)
      setConfirming(false)
      setThen({ kind: 'deleted' })
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'That flow could not be deleted. Try again.')
    } finally {
      setDeleting(false)
    }
  }

  const refusal = seeItRefusal(saved, host)
  const previewing = saved?.status === 'draft'
  const seeIt = refusal ? (
    <Button variant="outline" disabled title={refusal} data-see-it="off">
      See it <ExternalLink size={14} aria-hidden />
    </Button>
  ) : (
    <a
      href={seeItHref(saved!, host)}
      target="_blank"
      rel="noopener"
      data-see-it={previewing ? 'preview' : 'on'}
      className={buttonClass({ variant: 'outline' })}
      title={dirty ? 'Shows what is saved, not your unsaved changes.' : undefined}
    >
      See it <ExternalLink size={14} aria-hidden />
    </a>
  )

  const verifyOff = verifyMobileRefusal(values.ask, vocab)

  return (
    <EditPage
      title={saved ? saved.name : 'New booking flow'}
      description={FLOW_EXPLAINER}
      back={{ to: '/site/booking', label: 'Booking flows' }}
      meta={
        <div className="flex flex-wrap items-center gap-2">
          {saved && <StatusBadge kind="flow" status={saved.status} />}
          {saved && <span className="text-muted-foreground font-mono text-xs break-all">{saved.path}</span>}
          {(refusal || previewing) && (
            <span className="text-muted-foreground text-xs" data-see-it-reason="">
              {refusal ?? DRAFT_PREVIEW_NOTE}
            </span>
          )}
        </div>
      }
      actions={
        <>
          {seeIt}
          {saved && editable && (
            <Button variant="ghost" onClick={() => setConfirming(true)} data-delete-flow="">
              <Trash2 size={15} aria-hidden /> Delete
            </Button>
          )}
        </>
      }
    >
      {guard}
      {error && (
        <Alert tone="error">
          <span data-save-error="">{error}</span>
        </Alert>
      )}

      {/* -- name, address, status ----------------------------------------- */}
      <Card data-card="page">
        <CardBody>
          <div className={FORM_GRID}>
            <FormRow label="Name" htmlFor="flow-name">
              <Input
                id="flow-name"
                value={values.name}
                maxLength={120}
                disabled={!editable}
                placeholder="Tax seminar 2026"
                onChange={(e) => {
                  const name = e.target.value
                  setValues((prev) => ({ ...prev, name, slug: slugTouched ? prev.slug : slugFromName(name) }))
                }}
              />
            </FormRow>
            <FormRow label="Address" htmlFor="flow-slug">
              <div className="flex min-w-0 items-center gap-1">
                <span className="text-muted-foreground shrink-0 font-mono text-sm">/book/</span>
                <Input
                  id="flow-slug"
                  className="min-w-0 flex-1"
                  value={values.slug}
                  maxLength={64}
                  disabled={!editable}
                  placeholder="tax-seminar"
                  onChange={(e) => {
                    setSlugTouched(true)
                    set('slug', e.target.value)
                  }}
                />
              </div>
              {saved && values.slug.trim().toLowerCase() !== saved.slug && (
                <p className="text-muted-foreground mt-1 text-xs">
                  A website button that links to /book/{saved.slug} stops working when the address changes. Change
                  the button too.
                </p>
              )}
            </FormRow>
            <FormRow label="Status">
              <RadioGroup<FlowStatus>
                legend="Status"
                hideLegend
                value={values.status}
                onChange={(status) => editable && set('status', status)}
                options={(['draft', 'live', 'paused'] as const).map((value) => ({
                  value,
                  label: value === 'draft' ? 'Draft' : value === 'live' ? 'Live' : 'Paused',
                  description: FLOW_STATUS_NOTES[value],
                  disabled: !editable,
                }))}
              />
            </FormRow>
          </div>
        </CardBody>
      </Card>

      {/* -- 1. what we ask -------------------------------------------------- */}
      <Card data-card="ask">
        <CardBody className="space-y-4">
          <CardHead n={1} title="What we ask" note="Their details, on the first screen of the booking page." />
          <ul className="border-border divide-border divide-y rounded-lg border" data-fixed-rows="">
            <FixedRow label="Name" note="Always asked." />
            <FixedRow label="Email" note="Always asked, and confirmed with a code sent to it." />
          </ul>
          <SwitchRow
            label="Ask for mobile"
            note="Adds a mobile number to their details."
            name="mobile"
            checked={values.ask.mobile}
            disabled={!editable}
            onChange={(on) => setAsk(on ? { mobile: true } : { mobile: false, verify_mobile: false })}
          />
          <SwitchRow
            label="Verify mobile by code"
            note={verifyOff ?? 'A second code goes to their mobile, by your SMS or WhatsApp sender.'}
            reason={Boolean(verifyOff)}
            name="verify_mobile"
            checked={values.ask.verify_mobile}
            // Off when it cannot work — but a flow saved with it on can still be turned off.
            disabled={!editable || (Boolean(verifyOff) && !values.ask.verify_mobile)}
            onChange={(on) => setAsk({ verify_mobile: on })}
          />
          <Questions
            questions={values.ask.questions}
            vocab={vocab}
            editable={editable}
            onChange={(questions) => setAsk({ questions })}
          />
        </CardBody>
      </Card>

      {/* -- 2. what they can book ------------------------------------------ */}
      <Card data-card="offer">
        <CardBody className="space-y-4">
          <CardHead n={2} title="What they can book" note="The services and plans they choose from, with prices." />
          <Offer
            offer={values.offer}
            catalog={catalog}
            categories={categories}
            editable={editable}
            onChange={(offer) => set('offer', offer)}
          />
        </CardBody>
      </Card>

      {/* -- 3. documents ---------------------------------------------------- */}
      <DocumentsCard
        n={3}
        documents={values.documents ?? []}
        vocab={vocab}
        editable={editable}
        onChange={(documents) => set('documents', documents)}
      />

      {/* -- 4. payment ------------------------------------------------------ */}
      <PaymentCard
        n={4}
        values={values}
        vocab={vocab}
        editable={editable}
        mediaEditable={mediaEditable}
        onChange={(patch) => setValues((prev) => ({ ...prev, ...patch }))}
      />

      {/* -- 5. wording ------------------------------------------------------ */}
      <Card data-card="wording">
        <CardBody className="space-y-4">
          <CardHead n={5} title="Wording" note="The words on the booking page. Each starts filled in; change any." />
          <div className={FORM_GRID}>
            {WORDING_KEYS.map((key) => {
              const spec = vocab.wording[key]
              const long = key === 'intro' || key === 'thanks'
              const control = {
                id: `wording-${key}`,
                maxLength: spec?.max,
                value: values.wording[key],
                placeholder: spec?.default,
                disabled: !editable,
                hint: WORDING_NAMES[key].hint,
                'data-wording': key,
              }
              const change = (text: string) => set('wording', { ...values.wording, [key]: text })
              return (
                <FormRow key={key} label={WORDING_NAMES[key].label} htmlFor={control.id} optional={key === 'intro'}>
                  {long ? (
                    <Textarea {...control} rows={key === 'thanks' ? 3 : 2} onChange={(e) => change(e.target.value)} />
                  ) : (
                    <Input {...control} onChange={(e) => change(e.target.value)} />
                  )}
                </FormRow>
              )
            })}
          </div>
        </CardBody>
      </Card>

      <EditBar
        dirty={dirty}
        saving={saving}
        onSave={() => void save()}
        onDiscard={() => {
          setValues(flowValues(saved, vocab))
          setError(null)
        }}
        problem={dirty || !saved ? (problems[0] ?? null) : null}
        editable={editable}
        isNew={!saved}
      />

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Delete this booking flow?"
        confirmLabel="Delete"
        cancelLabel="Keep it"
        tone="danger"
        busy={deleting}
        busyLabel="Deleting…"
        error={deleteError}
        onConfirm={() => void remove()}
      >
        <p className="text-muted-foreground text-sm">
          Its address stops working, and a website button that links to it is no longer drawn. A flow somebody has
          already booked through is kept — pause it instead.
        </p>
      </ConfirmDialog>
    </EditPage>
  )
}

// -- small pieces ---------------------------------------------------------------

function FixedRow({ label, note }: { label: string; note: string }) {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2" data-fixed-row={label.toLowerCase()}>
      <span className="min-w-0">
        <span className="text-foreground block text-sm font-medium">{label}</span>
        <span className="text-muted-foreground block text-xs">{note}</span>
      </span>
      <Badge tone="neutral" size="sm">
        Always
      </Badge>
    </li>
  )
}

// -- the extra questions ---------------------------------------------------------

function Questions({
  questions,
  vocab,
  editable,
  onChange,
}: {
  questions: FlowQuestion[]
  vocab: FlowVocabulary
  editable: boolean
  onChange: (questions: FlowQuestion[]) => void
}) {
  const setAt = (index: number, next: FlowQuestion) => onChange(questions.map((q, i) => (i === index ? next : q)))
  const move = (index: number, by: -1 | 1) => {
    const to = index + by
    if (to < 0 || to >= questions.length) return
    const next = [...questions]
    const [question] = next.splice(index, 1)
    next.splice(to, 0, question as FlowQuestion)
    onChange(next)
  }
  const full = questions.length >= vocab.max_questions

  return (
    <div className="space-y-3" data-questions="">
      <div className="flex items-center justify-between gap-2">
        <p className="text-foreground text-sm font-medium">Extra questions</p>
        <span className="text-muted-foreground text-xs">
          {questions.length} of {vocab.max_questions}
        </span>
      </div>
      {questions.length === 0 && (
        <p className="text-muted-foreground text-xs">
          None yet. Add up to {vocab.max_questions}, if you need to know more.
        </p>
      )}
      {questions.map((question, index) => (
        <div key={question.key} className="border-border space-y-3 rounded-lg border p-3" data-question={index}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground text-xs font-medium">Question {index + 1}</span>
            {editable && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Move question ${index + 1} up`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp size={14} aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Move question ${index + 1} down`}
                  disabled={index === questions.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown size={14} aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove question ${index + 1}`}
                  onClick={() => onChange(questions.filter((_, i) => i !== index))}
                >
                  <Trash2 size={14} aria-hidden />
                </Button>
              </div>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
            <Input
              label="Question"
              maxLength={80}
              value={question.label}
              disabled={!editable}
              data-question-label={index}
              onChange={(e) => setAt(index, { ...question, label: e.target.value })}
            />
            <Select
              label="Answer"
              value={question.kind}
              disabled={!editable}
              data-question-kind={index}
              onChange={(e) => {
                const kind = e.target.value as QuestionKind
                const { options, ...rest } = question
                setAt(index, kind === 'select' ? { ...rest, kind, options: options ?? [] } : { ...rest, kind })
              }}
            >
              {vocab.question_kinds.map((kind) => (
                <option key={kind} value={kind}>
                  {QUESTION_KIND_LABELS[kind] ?? kind}
                </option>
              ))}
            </Select>
            {question.kind === 'select' && (
              <div className="sm:col-span-2">
                <Textarea
                  label="Options"
                  hint={`One per line, up to ${vocab.max_options}.`}
                  rows={3}
                  value={(question.options ?? []).join('\n')}
                  disabled={!editable}
                  data-question-options={index}
                  onChange={(e) => setAt(index, { ...question, options: e.target.value.split('\n') })}
                />
              </div>
            )}
          </div>
          <Checkbox
            label="An answer is required"
            checked={question.required}
            disabled={!editable}
            data-question-required={index}
            onChange={(e) => setAt(index, { ...question, required: e.target.checked })}
          />
        </div>
      ))}
      {editable && (
        <Button
          variant="secondary"
          size="sm"
          data-add-question=""
          disabled={full}
          title={full ? `A flow asks at most ${vocab.max_questions} extra questions.` : undefined}
          onClick={() =>
            onChange([...questions, { key: nextQuestionKey(questions), kind: 'text', label: '', required: false }])
          }
        >
          <Plus size={14} aria-hidden /> Add a question
        </Button>
      )}
    </div>
  )
}

// -- what they can book -------------------------------------------------------------

/**
 * Whether a service is **offered now** — it and its category both active
 * (C25, ADR-0054 §2: the one rule, `repos/services.offered`). Without the
 * categories (a caller from before C25) the category is read as active. A
 * flow may still *name* a service that is not offered; it is simply not shown
 * to visitors while it is not.
 */
export function offeredNow(service: Pick<Service, 'status' | 'category_id'>, categories?: ServiceCategory[] | null): boolean {
  if (service.status !== 'active') return false
  if (!categories) return true
  return categories.find((c) => c.id === service.category_id)?.status === 'active'
}

/** Why a service is not offered now, as the badge says it — or `null`. */
export function notOfferedBadge(
  service: Pick<Service, 'status' | 'category_id'>,
  categories?: ServiceCategory[] | null,
): string | null {
  if (offeredNow(service, categories)) return null
  if (service.status === 'deleted') return 'Deleted'
  return service.status === 'inactive' ? 'Inactive' : 'Category inactive'
}

/** The line under the services: what *none ticked* and *some ticked* offer,
 *  following the plan's website switch (ADR-0054 §5). */
export function offerNote(picked: number): string {
  return picked === 0
    ? 'None ticked: every service shown on your website is offered, with the plans your website shows.'
    : `${picked} ticked: only these are offered.`
}

function Offer({
  offer,
  catalog,
  categories,
  editable,
  onChange,
}: {
  offer: FlowOffer
  catalog: Service[] | null
  categories?: ServiceCategory[] | null
  editable: boolean
  onChange: (offer: FlowOffer) => void
}) {
  const picked = offer.services
  if (catalog === null) {
    return (
      <p className="text-muted-foreground text-sm" data-offer="unread">
        Your catalog could not be read, so what this flow offers is kept as it is
        {picked.length ? ` (${picked.length} services)` : ' (everything on your website)'}.
      </p>
    )
  }
  const listed = catalog.filter((s) => s.status !== 'deleted')
  const setServices = (services: FlowOffer['services']) => onChange({ services })
  const toggleService = (id: string, on: boolean) =>
    setServices(on ? [...picked, { service_id: id, plan_ids: [] }] : picked.filter((s) => s.service_id !== id))
  const gone = picked.filter((item) => !listed.some((s) => s.id === item.service_id))
  const paused = picked.filter((item) => {
    const service = listed.find((s) => s.id === item.service_id)
    return service !== undefined && !offeredNow(service, categories)
  })

  return (
    <div className="space-y-3" data-offer="">
      <p className="text-muted-foreground text-xs" data-offer-note="">
        {offerNote(picked.length)}
      </p>
      {listed.length === 0 ? (
        <p className="text-muted-foreground text-sm">Your catalog has no services yet.</p>
      ) : (
        <ul className="space-y-2">
          {listed.map((service) => {
            const item = picked.find((s) => s.service_id === service.id)
            const plans = (service.plans ?? []).filter((p) => p.status === 'active')
            const ticked = item ? tickedPlans(item, service) : []
            const badge = notOfferedBadge(service, categories)
            const hidden = plans.some((p) => !planShown(p))
            return (
              <li key={service.id} className="border-border rounded-lg border p-3" data-service={service.id}>
                <Checkbox
                  data-service-tick={service.id}
                  label={
                    <span>
                      {service.name}{' '}
                      {badge && (
                        <span data-not-offered={service.id}>
                          <Badge tone="warning" size="sm">
                            {badge}
                          </Badge>
                        </span>
                      )}{' '}
                      {!service.site_visible && (
                        <Badge tone="neutral" size="sm">
                          Not on your website
                        </Badge>
                      )}
                    </span>
                  }
                  checked={Boolean(item)}
                  disabled={!editable}
                  onChange={(e) => toggleService(service.id, e.target.checked)}
                />
                {item && (
                  <div className="mt-2 space-y-1 pl-6" data-plans={service.id}>
                    {plans.length === 0 ? (
                      <p className="text-muted-foreground text-xs">This service has no active plan.</p>
                    ) : (
                      plans.map((plan) => {
                        const on = ticked.includes(plan.id)
                        const last = on && ticked.length === 1
                        return (
                          <Checkbox
                            key={plan.id}
                            data-plan={plan.id}
                            label={
                              <span>
                                {plan.name}{' '}
                                {!planShown(plan) && (
                                  <span data-plan-hidden={plan.id}>
                                    <Badge tone="neutral" size="sm">
                                      Not on your website
                                    </Badge>
                                  </span>
                                )}
                              </span>
                            }
                            hint={last ? 'The last plan — untick the service instead.' : undefined}
                            checked={on}
                            disabled={!editable || last}
                            onChange={(e) => {
                              const next = togglePlan(item, service, plan.id, e.target.checked)
                              if (next) setServices(picked.map((s) => (s.service_id === service.id ? next : s)))
                            }}
                          />
                        )
                      })
                    )}
                    {hidden && (
                      <p className="text-muted-foreground text-xs" data-hidden-plans-note="">
                        A plan kept off your website is offered here only while it is ticked — a way to book it from
                        this flow’s link alone.
                      </p>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {paused.length > 0 && (
        <Alert tone="warning">
          <span data-offer-paused="">
            {paused.length === 1 ? 'One service' : `${paused.length} services`} this flow offers{' '}
            {paused.length === 1 ? 'is' : 'are'} not offered right now — inactive, or in an inactive category.
            Visitors do not see {paused.length === 1 ? 'it' : 'them'} until {paused.length === 1 ? 'it is' : 'they are'}{' '}
            active again.
          </span>
        </Alert>
      )}
      {gone.length > 0 && (
        <Alert tone="warning">
          <span data-offer-gone="">
            {gone.length === 1 ? 'One service' : `${gone.length} services`} this flow offers{' '}
            {gone.length === 1 ? 'is' : 'are'} no longer in your catalog, so the flow cannot be saved with{' '}
            {gone.length === 1 ? 'it' : 'them'}.{' '}
            {editable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setServices(picked.filter((s) => listed.some((c) => c.id === s.service_id)))}
              >
                Remove {gone.length === 1 ? 'it' : 'them'}
              </Button>
            )}
          </span>
        </Alert>
      )}
    </div>
  )
}
